// @ts-strict-ignore
import {
	communicationService,
	context,
	Dictionary,
	DiProcess,
	globalContextService,
	InstantConfigService,
	setupNpaContext,
	setupRdpContext,
	universalAdPackage,
	utils,
	CookieStorageAdapter,
} from '@wikia/ad-engine';
import { Injectable } from '@wikia/dependency-injection';
import { AD_ENGINE_UAP_LOAD_STATUS } from '../../../communication/events/events-ad-engine-uap';
import { NoAdsDetector } from '../services/no-ads-detector';

@Injectable()
export class BaseContextSetup implements DiProcess {
	constructor(
		protected instantConfig: InstantConfigService,
		protected noAdsDetector: NoAdsDetector,
	) {}

	execute(): void {
		this.setBaseState();
		this.setOptionsContext();
		this.setServicesContext();
		this.setMiscContext();
		this.setupStickySlotContext();
		this.setupDSAContext();
		setupNpaContext();
		setupRdpContext();
	}

	private setBaseState(): void {
		const SKIP_SRC_TEST_COOKIE_TTL_IN_MS = 86400000;

		if (utils.pageInIframe(window)) {
			this.noAdsDetector.addReason('in_iframe');
		}

		if (utils.client.isSteamPlatform()) {
			this.noAdsDetector.addReason('steam_browser');

			const topLeaderboard = document.querySelector('.top-leaderboard');
			topLeaderboard?.classList.remove('is-loading');

			const bottomLeaderboard = document.querySelector('.bottom-leaderboard');
			bottomLeaderboard?.classList.remove('is-loading');
		}
		if (utils.queryString.get('noexternals')) {
			this.noAdsDetector.addReason('noexternals_querystring');
		}
		if (utils.queryString.get('noads')) {
			this.noAdsDetector.addReason('noads_querystring');
		}

		const cookieAdapter = new CookieStorageAdapter();
		if (cookieAdapter.getItem('skipSrcTest')) {
			context.set('options.skipSrcTest', true);
		} else if (utils.queryString.get('skipSrcTest')) {
			context.set('options.skipSrcTest', true);
			cookieAdapter.setItem('skipSrcTest', '1', SKIP_SRC_TEST_COOKIE_TTL_IN_MS);
		}

		context.set('state.showAds', this.noAdsDetector.isAdsMode());
		context.set('state.deviceType', utils.client.getDeviceType());
		context.set('state.isLogged', !!context.get('wiki.wgUserId'));

		if (this.instantConfig.get('icPrebidium')) {
			context.set('state.provider', 'prebidium');
			communicationService.emit(AD_ENGINE_UAP_LOAD_STATUS, {
				isLoaded: false,
				adProduct: universalAdPackage.DEFAULT_UAP_TYPE,
			});
		}

		context.set('state.condition.roadblock.v2', this.instantConfig.get('stateRoadblockV2', false));
		context.set(
			'state.condition.hide.slot.placeholder',
			this.instantConfig.get('stateHideSlotPlaceholder', false),
		);
	}

	// https://fandom.atlassian.net/browse/ADEN-14861
	private configureOversizedAdsExperiment(): void {
		if (this.instantConfig.get('icExperiments', []).includes('oversizedAds')) {
			context.set('experiments.oversizedAds', {
				desktop: {
					top_leaderboard: [[728, 90]],
					top_boxad: [[300, 250]],
					incontent_boxad: [[300, 250]],
					incontent_leaderboard: [[728, 90]],
					bottom_leaderboard: [[728, 90]],
					gallery_leaderboard: [[728, 90]],
					mid_boxad: [[300, 250]],
				},
				mobile: {
					mobile_top_leaderboard: [[320, 50]],
					top_boxad: [[300, 250]],
					incontent_boxad: [[300, 250]],
					gallery_leaderboard: [[320, 50]],
					floor_adhesion: [[320, 50]],
					adhesion_limited: [[320, 50]],
					mobile_prefooter: [[300, 250]],
					bottom_leaderboard: [[320, 50]],
				},
			});
		} else {
			context.set('experiments.oversizedAds', {});
		}
	}

	private setOptionsContext(): void {
		this.setInContentExperiment();
		this.configureOversizedAdsExperiment();

		context.set('options.stickyTbExperiment', this.instantConfig.get('icStickyTbExperiment'));
		context.set(
			'options.uapExtendedSrcTargeting',
			this.instantConfig.get('icUAPExtendendSrcTargeting'),
		);

		context.set(
			'options.floorAdhesionNumberOfViewportsFromTopToPush',
			this.instantConfig.get('icFloorAdhesionViewportsToStart'),
		);
		context.set('options.rotatorDelay', this.instantConfig.get('icRotatorDelay', {}));
		context.set('options.maxDelayTimeout', this.instantConfig.get('icAdEngineDelay', 2000));
		context.set('options.delayEvents', this.instantConfig.get('icDelayEvents'));
		context.set(
			'options.iasPageTagging',
			this.instantConfig.get('icIasPageTagging', false) && window?.mw?.config?.get('wgIasEnabled'),
		);

		/* Turns on the use of the new 'no-ad' event that will be emitted when a banner ad doesn't have an ad fill from GAM.
		 * This option is directly related to "Productionalizing" the no-ad experiment - https://fandom.atlassian.net/browse/ADEN-14562.
		 * We can control this option/feature via the 'icSlotRefreshOnNoAd' InstantConfig variable, and expand it to other platforms if needed.
		 *
		 * This option also controls the max amount of consecutive retries for refreshing slots, when no ads come back from GAM, so it has a dual purpose.
		 *
		 * If this option is set to 0 or not defined for an application, then we will rely on the old 'collapse' event being emitted, which is currently not handled by the slot-refresher logic.
		 * */
		context.set('options.slotRefreshOnNoAd', this.instantConfig.get('icSlotRefreshOnNoAd', 0));

		/* This flag dictates which bidders will use the new GPID generator, so that PB is not hardcoded into it. */
		context.set('options.biddersWithNewGpid', this.instantConfig.get('icBiddersWithNewGpid', []));
		context.set(
			'options.forceDefaultAuSegOnAppNexus',
			this.instantConfig.get('icForceDefaultAuSegOnAppNexus'),
		);

		/* Turns on logic that prioritizes the use of the AdSlot.config.sizes when an ad is refreshed.
		 * If its not configured, then the usual AdSlot.config.defaultSizes will be appended on the refresh requests.
		 *
		 * By default, if this option is not turned on, we will always use the AdSlot.config.defaultSizes on refresh requests.
		 *
		 * This option can be set in ICBM (https://services.fandom.com/icbm/dashboard) on a per application basis ex: ucp-desktop, ucp-mobile etc.
		 * */
		context.set(
			'options.useAdSizesOnRefresh',
			this.instantConfig.get('icUseAdSizesOnRefresh', false),
		);

		this.setupVideo();
		this.setWadContext();
	}

	private setupVideo(): void {
		context.set(
			'options.video.playAdsOnNextVideo',
			!!this.instantConfig.get('icFeaturedVideoAdsFrequency'),
		);
		context.set(
			'options.video.adsOnNextVideoFrequency',
			this.instantConfig.get('icFeaturedVideoAdsFrequency', 3),
		);
		context.set('options.video.isMidrollEnabled', this.instantConfig.get('icFeaturedVideoMidroll'));
		context.set(
			'options.video.isPostrollEnabled',
			this.instantConfig.get('icFeaturedVideoPostroll'),
		);
		context.set('options.jwpMaxDelayTimeout', this.instantConfig.get('icUAPJWPlayerDelay', 0));
		context.set('options.video.iasTracking.enabled', this.instantConfig.get('icIASVideoTracking'));
		context.set('options.video.syncWithDisplay', this.instantConfig.get('icUAPJWPlayer'));
		context.set(
			'options.video.displayAndVideoAdsSyncEnabled',
			this.instantConfig.get('icDisplayAndVideoAdsSyncEnabled'),
		);
		context.set(
			'options.video.uapJWPLineItemIds',
			this.instantConfig.get('icUAPJWPlayerLineItemIds'),
		);
		context.set(
			'options.video.comscoreJwpTracking',
			this.instantConfig.get('icComscoreJwpTracking'),
		);
		context.set(
			'options.video.experiments.disabled.incontentPlayerRemoval',
			this.instantConfig.get('icDisableIncontentPlayerRemoval'),
		);
		context.set(
			'services.anyclip.enabled',
			this.instantConfig.get('icAnyclipPlayer') && !this.instantConfig.get('icConnatixPlayer'),
		);
		context.set('services.anyclip.isApplicable', () => {
			return !context.get('custom.hasFeaturedVideo') && !this.instantConfig.get('icConnatixPlayer');
		});
		context.set('services.connatix.enabled', this.instantConfig.get('icConnatixPlayer'));

		if (this.instantConfig.get('icJwpInSecondaryPlayer', false)) {
			context.set('services.jwp.config', window.ads.context?.opts?.jwpIcb ?? {});
		}

		if (this.instantConfig.get('icJwpDrawerPlayer', false)) {
			context.set('services.jwpDrawerPlayer', window.ads.context?.opts?.jwpIcb ?? {});
		}
	}

	private setInContentExperiment(): void {
		const excludedBundleTagName = 'sensitive';
		const communityExcludedByTag = globalContextService.hasBundle(excludedBundleTagName);

		const isMobile = context.get('state.isMobile');
		const isInContentHeadersExperiment = this.instantConfig
			.get('icExperiments', [])
			.includes('incontentHeaders');

		if (isInContentHeadersExperiment && !communityExcludedByTag && !isMobile) {
			context.set('templates.incontentHeadersExperiment', true);
		} else {
			context.set('templates.incontentAnchorSelector', '.mw-parser-output > h2');
		}
	}

	private setWadContext(): void {
		const babEnabled = this.instantConfig.get('icBabDetection');

		// BlockAdBlock detection
		context.set('options.wad.enabled', babEnabled);

		if (babEnabled && !context.get('state.isLogged') && context.get('state.showAds')) {
			context.set('options.wad.btRec.enabled', this.instantConfig.get('icBTRec'));
			context.set('options.wad.btRec.sideUnits', this.instantConfig.get('icBTRecSideUnits'));
		}
	}

	private setServicesContext(): void {
		context.set(
			'services.interventionTracker.enabled',
			this.instantConfig.get('icInterventionTracking'),
		);
		context.set('services.nativo.enabled', this.instantConfig.get('icNativo'));
		context.set('services.ppid.enabled', this.instantConfig.get('icPpid'));
		context.set('services.ppidRepository', this.instantConfig.get('icPpidRepository'));
		context.set('services.identityTtl', this.instantConfig.get('icIdentityTtl'));
		context.set('services.identityPartners', this.instantConfig.get('icIdentityPartners'));

		context.set(
			'services.messageBox.enabled',
			this.instantConfig.get('icAdCollapsedMessageBox', false),
		);
		context.set('services.slotRefresher.config', this.instantConfig.get('icSlotRefresher'));
	}

	private setMiscContext(): void {
		context.set(
			'bidders.prebid.disableSendAllBids',
			this.instantConfig.get('icPrebidDisableSendAllBids'),
		);
		context.set('bidders.prebid.config', this.instantConfig.get('icPrebidConfig', {}));
		context.set('bidders.prebid.native.enabled', this.instantConfig.get('icPrebidNative'));
		context.set(
			'templates.sizeOverwritingMap',
			universalAdPackage.UAP_ADDITIONAL_SIZES.companionSizes,
		);
		context.set('bidders.s2s.bidders', this.instantConfig.get('icPrebidS2sBidders', []));
		context.set('bidders.s2s.enabled', this.instantConfig.get('icPrebidS2sBidders', []).length > 0);

		context.set('bidders.a9.hem.enabled', this.instantConfig.get('icA9HEM', false));
		context.set('bidders.a9.hem.cleanup', this.instantConfig.get('icA9CleanHEM', false));

		context.set(
			'bidders.liveIntentConnectedId.enabled',
			this.instantConfig.get('icLiveIntentConnectedId'),
		);

		context.set('gumgum.floorAdhesion', this.instantConfig.get('icGumgumFloorAdhesion'));
		context.set('kargo.floorAdhesion', this.instantConfig.get('icKargoFloorAdhesion'));
		context.set('seedtag.floorAdhesion', this.instantConfig.get('icSeedtagFloorAdhesion'));
	}

	private setupStickySlotContext(): void {
		context.set('templates.stickyTlb.forced', this.instantConfig.get('icForceStickyTlb'));
		context.set('templates.stickyTlb.withFV', this.instantConfig.get('icStickyTlbWithFV'));

		const stickySlotsLines: Dictionary = this.instantConfig.get('icStickySlotLineItemIds');
		if (stickySlotsLines && stickySlotsLines.length) {
			context.set('templates.stickyTlb.lineItemIds', stickySlotsLines);
		}
	}

	private setupDSAContext(): void {
		context.set('options.dsa.enabled', this.instantConfig.get('icDSA', false));
		context.set('options.dsa.modal.enabled', this.instantConfig.get('icDSAModal', false));
	}
}
